// Enhanced utility functions for cleaning text content in chat messages

import { isEnhancedFeedbackAnalysis } from './enhancedFeedbackDetection';

export const cleanAIResponse = (text: string) => {
  console.log('🧹 textCleaningUtils: Starting cleanAIResponse');
  console.log('🧹 textCleaningUtils: Input preview:', text.substring(0, 300));
  
  // CRITICAL: Use enhanced feedback detection and COMPLETELY bypass processing
  if (isEnhancedFeedbackAnalysis(text)) {
    console.log('🎯 textCleaningUtils: Enhanced feedback detected - ZERO processing applied');
    console.log('🎯 textCleaningUtils: Returning content completely unchanged for feedback renderer');
    console.log('🎯 textCleaningUtils: Line count preserved:', text.split('\n').length);
    return text; // Return completely unprocessed content
  }
  
  // CRITICAL: Detect welcome message and preserve ALL formatting
  if (text.includes('Welcome to RemoteViz!') && text.includes('Your journey into remote viewing starts here')) {
    console.log('🏡 textCleaningUtils: Welcome message detected - preserving ALL formatting');
    return text; // Return completely unchanged
  }
  
  // Try to parse as JSON only for non-feedback content
  const jsonResponse = tryParseTargetJSON(text);
  if (jsonResponse) {
    console.log('🧹 textCleaningUtils: JSON response detected');
    
    if (jsonResponse.error) {
      return "I apologize, but I encountered an issue while generating your target. Please try again in a moment.";
    }
    
    if (jsonResponse.target_code && jsonResponse.message) {
      console.log('🧹 textCleaningUtils: JSON target response with message');
      
      // Check if the extracted message is feedback analysis
      if (isEnhancedFeedbackAnalysis(jsonResponse.message)) {
        console.log('🎯 textCleaningUtils: JSON message is feedback - returning unchanged');
        return jsonResponse.message; // No processing for feedback in JSON
      }
      
      return jsonResponse.message;
    }
    
    return "I've processed your request. Please let me know if you need any clarification.";
  }

  // Only apply aggressive cleaning to specific target generation responses
  if (text.includes('The target for code') && text.includes('is:')) {
    let cleanedText = text.replace(/The target for code [A-Z0-9]{4}-[A-Z0-9]{4} is:\s*Image\s*/gi, '');
    cleanedText = cleanedText.replace(/\b(Image)\b\s*$/gi, '');
    cleanedText = cleanedText.replace(/[ \t]+/g, ' ');
    cleanedText = cleanedText.replace(/\n{3,}/g, '\n\n');
    return cleanedText.trim();
  }
  
  // For regular chat messages, preserve markdown formatting - only clean excessive whitespace
  return text.replace(/\n{4,}/g, '\n\n\n').trim();
};

// Simplified JSON detection that won't interfere with feedback analysis
const tryParseTargetJSON = (text: string): any | null => {
  try {
    // Don't try to parse feedback analysis as JSON
    if (isEnhancedFeedbackAnalysis(text)) {
      return null;
    }

    let cleanContent = text.trim();
    
    // Remove markdown code block formatting
    if (cleanContent.startsWith('```json')) {
      cleanContent = cleanContent.replace(/^```json\s*/, '').replace(/\s*```$/, '');
    } else if (cleanContent.startsWith('```')) {
      cleanContent = cleanContent.replace(/^```\s*/, '').replace(/\s*```$/, '');
    }

    // Extract JSON from "json {}" pattern
    const jsonMatch = text.match(/json\s*(\{[\s\S]*?\})/);
    if (jsonMatch) {
      cleanContent = jsonMatch[1];
    }

    // Only parse if it looks like JSON and could be a target/error response
    if (cleanContent.startsWith('{') && cleanContent.endsWith('}')) {
      const parsed = JSON.parse(cleanContent);
      
      // Validate this is a target generation or error response
      if (parsed.target_code || parsed.error || parsed.message) {
        return parsed;
      }
    }
    
    return null;
  } catch (error) {
    return null;
  }
};

// Enhanced link processing that completely skips feedback analysis
export const processContentForLinks = (text: string): string => {
  // CRITICAL: Skip ALL processing for feedback analysis using enhanced detection
  if (isEnhancedFeedbackAnalysis(text)) {
    console.log('🧹 processContentForLinks: Enhanced feedback detected - skipping ALL processing');
    console.log('🧹 processContentForLinks: Line count preserved:', text.split('\n').length);
    return text;
  }

  // CRITICAL: Detect welcome message and preserve ALL formatting
  if (text.includes('Welcome to RemoteViz!') && text.includes('Your journey into remote viewing starts here')) {
    console.log('🏡 processContentForLinks: Welcome message detected - preserving ALL formatting');
    return text; // Return completely unchanged
  }

  // Skip if already contains markdown links
  if (text.includes('](') && text.includes('[')) {
    return text;
  }

  // Enhanced URL regex for complex query parameters
  const urlPattern = /(https?:\/\/[^\s\)\]\<\>\"\']+)/gi;
  let processedText = text;

  // Process URLs but preserve image URLs for separate rendering
  processedText = processedText.replace(urlPattern, (match) => {
    // Skip if already in markdown format
    if (text.includes(`[${match}]`) || text.includes(`](${match})`)) {
      return match;
    }
    
    // Remove image URLs from text since they're rendered separately
    if (match.toLowerCase().includes('image') || 
        ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'].some(ext => match.toLowerCase().includes(ext)) ||
        match.includes('unsplash.com') || 
        match.includes('images.')) {
      return '';
    }
    
    // Convert to markdown link
    return `[${match}](${match})`;
  });

  // Clean up whitespace left by removed image URLs - preserve line breaks
  processedText = processedText.replace(/[ \t]+/g, ' ').trim();
  
  return processedText;
};
